import { MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { AssessmentInstanceDto, AssessmentPlayerMode, AssessmentQuestionDto, AssessmentSaveResultDto, AssessmentStartPlayerDto } from "generated/CoreApi_v1";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssPlayerBase, AssPlayerMode } from "views/assessment/player/AssPlayerBase";

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    assessmentId: string;
    deployId?: string;
    deployParticipantId?: string;
    mode: AssPlayerMode;
    onClose: (success: boolean) => void;
}

/***
 *   Player para pesquisa.
 *   assessmentId           => Obrigatório para modo preview e para validar se o deploy é do assessment.
 *   deployId               => Quando não for mode revisão, parâmetro obrigatório pegar o contexto para apresentar o resultado.
 *   deployParticipantId    => Será obrigatório para pesquisas do tipo enrollment / nulo para criar um participante para pesquisa adhoc.
 *   mode:                  => Para identificar se é para iniciar em modo de preechimento de resposta, revisão final ou visualização.
*/
export const AssPlayerSurvey: React.FC<IPros> = (props) => {
    const { assessmentId, deployId, deployParticipantId, mode, onClose } = props;

    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("assessment");

    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [startPlayer, setStartPlayer] = useState<AssessmentStartPlayerDto>();

    useEffect(() => {
        async function getData() {
            try {
                if (mode == "preview") {
                    setStartPlayer({
                        deployId: null,
                        startDate: moment().toDate(),
                        endDate: moment().toDate(),
                        playerMode: AssessmentPlayerMode.NewInstance,
                        instanceId: null,
                        participantId: null,
                        message: null
                    });
                }
                else if (deployId) {
                    var result = await api.v1.assPlayer.getStartPlayerSurvey({
                        assessmentId,
                        deployId: deployId,
                        participantId: deployParticipantId ?? null,
                        onlyResult: mode == "finalResult"
                    });
                    setStartPlayer(result.data);
                }
            } catch (e) {
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();

    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    if (!loading && startPlayer && startPlayer.playerMode == AssessmentPlayerMode.NoAccess) {
        enqueueSnackbar(startPlayer.message ?? t("AssPlayer.Survey.CannotAccessMessage"), { variant: "error" });
        return null;
    }

    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => onClose(false)} />

            <MprcRequestIndicator type="request" visible={loading} />

            {
                !loading && startPlayer &&
                <AssPlayerBase
                    instanceId={startPlayer.instanceId}
                    startPlayerMode={startPlayer.playerMode}
                    deployId={deployId}
                    createInstance={() =>
                        new Promise<AssessmentInstanceDto>((resolve, reject) => {
                            api.v1.assPlayer.createSurveyInstance({
                                assessmentId: assessmentId!,
                                preview: mode == "preview",
                                deployId: deployId ?? null,
                                deployParticipantId: startPlayer?.participantId ?? deployParticipantId ?? null
                            })
                                .then(result => resolve(result.data))
                                .catch(e => reject(e));
                        })
                    }
                    startInstance={(instanceId) =>
                        new Promise<AssessmentSaveResultDto>((resolve, reject) => {
                            api.v1.assPlayer.startSurveyInstance({ instanceId, deployId: deployId ?? null, deployParticipantId: startPlayer?.participantId ?? deployParticipantId ?? null })
                                .then(result => resolve(result.data))
                                .catch(e => reject(e));
                        })
                    }
                    getStartPlayer={(playerMode, instanceId) => {
                        if (playerMode == AssessmentPlayerMode.FinalResult && deployId && mode != "preview") {
                            return new Promise<AssessmentInstanceDto>((resolve, reject) => {
                                api.v1.assPlayer.getDeploySummary({ deployId: deployId, instanceId: instanceId ?? null })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            });
                        } else {
                            if (instanceId) {
                                return new Promise<AssessmentInstanceDto>((resolve, reject) => {
                                    api.v1.assPlayer.getSurveyInstance({ instanceId, playerMode: playerMode ?? startPlayer.playerMode })
                                        .then(result => resolve(result.data))
                                        .catch(e => reject(e));
                                });
                            } else {
                                throw new Error('Instance is required!');
                            }
                        }
                    }}
                    getQuestion={(action, playerMode, instanceId, questionIdRef, versionRef) => {
                        if (playerMode == AssessmentPlayerMode.FinalResult && deployId) {
                            return new Promise<AssessmentQuestionDto>((resolve, reject) => {
                                api.v1.assPlayer.getDeployQuestion({
                                    deployId: deployId,
                                    instanceId: instanceId ?? null,
                                    questionIdRef: questionIdRef ?? null,
                                    versionRef: versionRef ?? null,
                                    action
                                })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            });
                        }
                        else if (instanceId) {
                            return new Promise<AssessmentQuestionDto>((resolve, reject) => {
                                api.v1.assPlayer.getQuestionAssInstance({
                                    instanceId: instanceId,
                                    questionIdRef: questionIdRef ?? null,
                                    versionRef: versionRef ?? null,
                                    playerMode,
                                    action
                                })
                                    .then(result => resolve(result.data))
                                    .catch(e => reject(e));
                            });
                        } else {
                            throw new Error('Instance is required!');
                        }
                    }}
                    onClose={onClose}
                />
            }
        </>
    );
};
